import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/apiService';

const CallerTravelPost = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [callStatus, setCallStatus] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({ id: '', description: '' });
  const [openDropdown, setOpenDropdown] = useState(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: searchKeyword,
        date: startDate && endDate ? `${startDate} - ${endDate}` : '',
        callStatus: callStatus
      };
      const response = await apiService.post('travelPostList', params);
      if (response && response.data) {
        setPosts(response.data);
        setTotalRecords(response.recordsTotal || 0);
      }
    } catch (error) {
      toast.error('Failed to load travel posts');
      console.error('Travel posts fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, searchKeyword, startDate, endDate, callStatus]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchPosts();
    }, searchKeyword ? 500 : 0);
    return () => clearTimeout(timeoutId);
  }, [fetchPosts]);

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    setCurrentPage(1);
    setShowDatePicker(false);
  };

  const clearDateRange = () => {
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    setShowDatePicker(false);
  };

  const handleEdit = (post) => {
    setEditData({ id: post._id, description: post.description || '' });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await apiService.post('editTravelPost', editData);
      if (response.status || !response.success) {
        toast.success('Travel post updated successfully');
        setShowEditModal(false);
        fetchPosts();
      } else {
        toast.error(response.message || 'Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update travel post');
    }
  };

  const updateCallStatus = async (id, status) => {
    try {
      const response = await apiService.post(`updateCallStatus/${id}`, { callStatus: status });
      if (response.status) {
        toast.success('Call status updated successfully');
        fetchPosts();
      } else {
        toast.error(response.message || 'Failed to update call status');
      }
    } catch (error) {
      toast.error('Failed to update call status');
    }
    setOpenDropdown(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  };

  const getCallStatusBadge = (status) => {
    const config = {
      pending: 'bg-yellow-100 text-yellow-800',
      connected: 'bg-green-100 text-green-800',
      not_connected: 'bg-red-100 text-red-800',
      busy: 'bg-[#009ef7]/10 text-[#009ef7]',
      no_answer: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      pending: 'Pending',
      connected: 'Connected',
      not_connected: 'Not Connected',
      busy: 'Busy',
      no_answer: 'No Answer'
    };
    return { color: config[status] || config.pending, label: labels[status] || 'Pending' };
  };

  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-0">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Header */}
          <div className="bg-gray-50 p-6 border-b border-gray-200">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="relative w-64">
                <i className="bi bi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  placeholder="Type Name, Phone or Email"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                />
              </div>

              <div className="relative">
                <div className="cursor-pointer relative" onClick={() => setShowDatePicker(!showDatePicker)}>
                  <input
                    type="text"
                    placeholder="Pick date range"
                    value={startDate && endDate ? `${startDate} - ${endDate}` : ''}
                    readOnly
                    className="w-48 pl-4 pr-10 py-2 border border-gray-300 rounded-lg cursor-pointer"
                  />
                  <i className="bi bi-calendar3 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                </div>
                {showDatePicker && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4 w-80">
                    <div className="flex gap-4 mb-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">From:</label>
                        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">To:</label>
                        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <button onClick={clearDateRange} className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200">Clear</button>
                      <button onClick={() => handleDateChange(startDate, endDate)} disabled={!startDate || !endDate} className="px-3 py-1 text-sm bg-[#009ef7] text-white rounded hover:bg-[#009ef7]/90 disabled:opacity-50">Apply</button>
                    </div>
                  </div>
                )}
              </div>

              <select value={callStatus} onChange={(e) => setCallStatus(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7]">
                <option value="">All Call Status</option>
                <option value="pending">Pending</option>
                <option value="connected">Connected</option>
                <option value="not_connected">Not Connected</option>
                <option value="busy">Busy</option>
                <option value="no_answer">No Answer</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009ef7]"></div>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">No Travel Posts Found</h3>
                <p className="text-gray-500">No posts match your current search criteria.</p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source / Destination</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Buyers</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Call Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {posts.map((post, index) => {
                    const user = post.userId || {};
                    const leavingFrom = post.leavingFrom?.[0];
                    const goingTo = post.goingTo?.[0];
                    const statusBadge = getCallStatusBadge(post.callStatus);
                    
                    return (
                      <tr key={post._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-[#009ef7]">{(currentPage - 1) * recordsPerPage + index + 1}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{user.first_name} {user.last_name}</td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex flex-col">
                            <span className="text-gray-900">{user.email || '-'}</span>
                            <span className="text-gray-600 text-xs">{user.phone || '-'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex flex-col gap-1">
                            <span className="text-gray-900">{leavingFrom?.name || '-'}</span>
                            <div className="flex items-center gap-1">
                              <span className="text-gray-900">{goingTo?.name || '-'}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs">
                          <div className="truncate" title={post.description}>{post.description || '-'}</div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="relative inline-block">
                            <button onClick={() => setOpenDropdown(openDropdown === `buyers-${post._id}` ? null : `buyers-${post._id}`)} className="px-3 py-1 bg-[#009ef7]/10 text-[#009ef7] rounded text-xs font-semibold hover:bg-blue-200">
                              {post.buyBy?.length || 0} Vendor{post.buyBy?.length > 1 ? 's' : ''}
                            </button>
                            {openDropdown === `buyers-${post._id}` && (
                              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-48">
                                {post.buyBy?.length > 0 ? post.buyBy.map((vendor, i) => (
                                  <div key={i} className="px-2 sm:px-4 py-2 text-sm border-b last:border-b-0">
                                    <div className="font-semibold text-gray-900">{vendor.business_name || '-'}</div>
                                    <div className="text-gray-600">{vendor.first_name} {vendor.last_name}</div>
                                  </div>
                                )) : <div className="px-2 sm:px-4 py-2 text-sm text-gray-500">No vendors</div>}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="relative inline-block">
                            <button onClick={() => setOpenDropdown(openDropdown === `status-${post._id}` ? null : `status-${post._id}`)} className={`px-3 py-1 rounded text-xs font-semibold ${statusBadge.color}`}>
                              {statusBadge.label}
                            </button>
                            {openDropdown === `status-${post._id}` && (
                              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-40">
                                {['pending', 'connected', 'not_connected', 'busy', 'no_answer'].map(status => (
                                  <button key={status} onClick={() => updateCallStatus(post._id, status)} className="block w-full text-left px-2 sm:px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                    {getCallStatusBadge(status).label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          <div className="flex flex-col">
                            <span className="font-semibold">{new Date(post.createdAt).toLocaleDateString('en-IN')}</span>
                            <span className="text-xs text-gray-500">{new Date(post.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {!loading && posts.length > 0 && (
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs sm:text-sm text-gray-700">Show</span>
                <select value={recordsPerPage} onChange={(e) => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="px-3 py-1 border border-gray-300 rounded-lg text-xs sm:text-sm">
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-700">entries</span>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 hover:bg-gray-100">«</button>
                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                    return (
                      <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 border rounded text-sm ${currentPage === page ? 'bg-[#009ef7] text-white border-[#009ef7]' : 'border-gray-300 hover:bg-gray-100'}`}>{page}</button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="px-2 text-gray-500">...</span>;
                  }
                  return null;
                })}
                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 hover:bg-gray-100">»</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Update Travel Post</h2>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x text-2xl"></i>
              </button>
            </div>
            <form onSubmit={handleEditSubmit} className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Description <span className="text-red-500">*</span></label>
                <textarea value={editData.description} onChange={(e) => setEditData({ ...editData, description: e.target.value })} rows="8" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]" required />
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowEditModal(false)} className="px-2 sm:px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
                <button type="submit" className="px-2 sm:px-4 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#009ef7]/90">Submit</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallerTravelPost;
